import { AssistantAvailabilityBlock, AssistantAvailabilityBlockState, Service } from '@mews-commander/data-access';
import { TextKey, RouteName } from '@mews-commander/enums';
import { GridItem, Tag } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { LocalizedText, useLocalizeProperty } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { getFullName, useCommanderSelector } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getService } from '@mews-commander/legacy-entities/src/ducks/services';
import { navigateInBaseModal } from '../../../app/actions/common';
import { ResultListProps } from '../results/ListTitle';
import { stringifyDateRange } from '../../utils/array';
import { highlight } from '../../utils/highlight';
import { useRateGetQuery } from '../../../rates';
import { SearchType } from '../../assistantSearchTypes';
import { DescriptionItem, SearchItemDescription } from '../components/SearchItemDescription';
import { useLocalizedDate } from './useLocalizedDate';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface AvailabilityBlockListProps extends ResultListProps {
    availabilityBlocks: AssistantAvailabilityBlock[];
}

const availabilityBlockTypeTextKey = {
    [AssistantAvailabilityBlockState.Confirmed]: TextKey.AssistantAvailabilityBlockStateConfirmed,
    [AssistantAvailabilityBlockState.Optional]: TextKey.AssistantAvailabilityBlockStateOptional,
    [AssistantAvailabilityBlockState.Inquired]: TextKey.AssistantAvailabilityBlockStateInquired,
    [AssistantAvailabilityBlockState.Canceled]: TextKey.AssistantAvailabilityBlockStateCanceled,
};

interface PrimaryTextProps {
    availabilityBlock: AssistantAvailabilityBlock;
    searchTerm: string;
}

const PrimaryText = ({ availabilityBlock, searchTerm }: PrimaryTextProps) => {
    const text = highlight(availabilityBlock.name ?? '', searchTerm);
    return <>{text}</>;
};

interface AvailabilityBlockListItemProps extends ResultListProps {
    availabilityBlock: AssistantAvailabilityBlock;
    enterpriseId: string;
}

const SecondaryText = ({ availabilityBlock, searchTerm, enterpriseId }: AvailabilityBlockListItemProps) => {
    const { startDate, endDate, service, rateId } = availabilityBlock;
    const formattedStartDate = useLocalizedDate(startDate);
    const formattedEndDate = useLocalizedDate(endDate);

    let serviceAndRate = '';
    let serviceId = '';

    const foundService: Service | undefined = useCommanderSelector((state) => getService(state, { id: service.id }));
    const localizeProperty = useLocalizeProperty();
    const serviceName = localizeProperty(foundService?.names);
    if (foundService) {
        serviceAndRate = serviceName;
        serviceId = foundService.id;
    }

    const { data: rate } = useRateGetQuery(
        {
            enterpriseIds: [enterpriseId],
            rateId: rateId || '',
            serviceIds: [serviceId],
        },
        { enabled: !!rateId && !!serviceId }
    );

    if (rate) {
        serviceAndRate += `${serviceAndRate}${UnicodeEntity.NoBreakSpace}${UnicodeEntity.LeftParenthesis}${rate.name}${UnicodeEntity.RightParenthesis}`;
    }

    const dates: string = stringifyDateRange([formattedStartDate, formattedEndDate]);
    const booker = getFullName(availabilityBlock.booker) ?? '';

    const descriptions: DescriptionItem[] = [
        { value: highlight(booker, searchTerm) },
        { value: highlight(dates, searchTerm) },
        ...(rate ? [{ value: highlight(serviceAndRate, searchTerm) }] : []),
    ];
    return <SearchItemDescription descriptions={descriptions} />;
};

const RightItem = ({ availabilityBlock }: Pick<AvailabilityBlockListItemProps, 'availabilityBlock'>) => (
    <GridItem alignSelf="end">
        <Tag variant="primary">
            <LocalizedText textKey={availabilityBlockTypeTextKey[availabilityBlock.state]} />
        </Tag>
    </GridItem>
);

interface AvailabilityBlockListItemProps extends ResultListProps {
    availabilityBlock: AssistantAvailabilityBlock;
}

export const useAvailabilityBlockList = ({
    availabilityBlocks,
    searchTerm,
}: AvailabilityBlockListProps): AssistantSearchListItemProps[] => {
    const resolveRoute = useResolveRoute();
    const enterpriseId = useCurrentEnterpriseId();

    return availabilityBlocks.map((availabilityBlock) => {
        const url = resolveRoute(RouteName.AvailabilityBlockDetail, {
            availabilityBlockId: availabilityBlock.id,
            query: { OwnerId: availabilityBlock.service.id },
        });

        return {
            itemId: availabilityBlock.id,
            primaryText: <PrimaryText availabilityBlock={availabilityBlock} searchTerm={searchTerm} />,
            secondaryText: (
                <SecondaryText
                    availabilityBlock={availabilityBlock}
                    searchTerm={searchTerm}
                    enterpriseId={enterpriseId}
                />
            ),
            routeAction: navigateInBaseModal(url),
            rightItem: <RightItem availabilityBlock={availabilityBlock} />,
            searchType: SearchType.AvailabilityBlock,
        };
    });
};
